<template>
  <div class="catalog-filters-mobile">
    <UIMobileFullScreenDouble
      v-model:show-second-screen="secondScreenIsShown"
      :title="$t('components.catalog.catalogFiltersMobile.catalogFiltersMobile.title')"
      :second-screen-title="secondScreenTitle"
      :loading="loading"
      @close="emit('close')"
    >
      <template #first-screen>
        <CatalogFiltersMobileFirstScreen
          :model-value="secondScreenType"
          :where-are-we="whereAreWe"
          @update:model-value="openSecondScreen"
        />
      </template>

      <template #second-screen>
        <CatalogFiltersMobileSecondScreen
          v-if="secondScreenType"
          :screen-type="secondScreenType"
          :show-all-categories="showAllCategories"
        />
      </template>

      <div class="catalog-filters-mobile__buttons">
        <UIButton
          size="l"
          type="default"
          class="catalog-filters-mobile__button"
          @click="clearAllFilters(currentCategoryId, weInsideBrand)"
        >
          {{ $t('components.catalog.catalogFiltersMobile.catalogFiltersMobile.clearAll') }}
        </UIButton>
        <UIButton
          size="l"
          type="black"
          class="catalog-filters-mobile__button"
          @click="showProducts"
        >
          {{ $t('common.show') }} ({{ availableFilters.productCount }})
        </UIButton>
      </div>
    </UIMobileFullScreenDouble>
  </div>
</template>

<script lang="ts" setup>
import {
  useAvailableFilters,
  useStateAttributes,
  useStateAvailableFilters,
  useStateSelectedFilters,
} from '~/services/catalogFilter/useAvailableFilters';
import { useCatalogFilters } from '~/services/catalogFilter/useCatalogFilters';
import { TCheckboxQueryParamName } from '~/types/filters';
import UIMobileFullScreenDouble from '~/components/ui/UIMobileFullScreenDouble.vue';
import UIButton from '~/components/ui/UIButton.vue';
import { isDefined, isNumber } from '~/utils/extra';
import { useCategoriesFilter } from '~/services/catalogFilter/categories';
import { useSizesFilter } from '~/services/catalogFilter/sizes';
import { useCategoryTree } from '~/store/categoryTree';
import { useCatalogProvider } from '~/providers/catalog';
import { useBrands } from '~/services/catalogFilter/brands';
import { useProductConditions } from '~/services/catalogFilter/productConditions';
import { useSorting } from '~/services/catalogFilter/sorting';
import CatalogFiltersMobileSecondScreen from './CatalogFiltersMobileSecondScreen.vue';
import CatalogFiltersMobileFirstScreen from './CatalogFiltersMobileFirstScreen.vue';

const props = defineProps<{
  whereAreWe: TCatalogPlacing,
  pageInfo?: Record<string, string>,
  currentCategoryId?: number,
  showAllCategories?: boolean
  profileId?: number
}>();

const emit = defineEmits<{
  (e: 'close'): void
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { account } = useAccountStore();

const { setFilters } = useCatalogFilters(route, router);
const { loadCategoryTree, getCategoryById } = useCategoryTree();
const { loadAllBrands } = useBrands();
const { loadProductConditions } = useProductConditions();
const availableFilters = useStateAvailableFilters();
const selectedFilters = useStateSelectedFilters();
const attributes = useStateAttributes();
const { fetchSizes, fetchAttributes } = useCatalogProvider();
const { getAvailableFilters, clearAllFilters } = useAvailableFilters(route);
const { selectedCategories, availableCategories } = useCategoriesFilter(route);
const { allSizes, availableSizes } = useSizesFilter();
const { currentSortingMethod } = useSorting(route, router);

interface IScreenType {
  type: TCheckboxQueryParamName
  id: TCheckboxQueryParamName | number
}

const secondScreenIsShown = ref(false);
const secondScreenType = ref<IScreenType>();
const categoryId = computed(() => {
  const [id] = selectedFilters.value.category;
  const category = getCategoryById(id);

  if (!category) {
    return id;
  }

  return category.children ? category.id : category.parentId;
});
const weInsideCatalog = props.whereAreWe === 'catalog';
const weInsideBrand = props.whereAreWe === 'brand';
const categoryIdIsAvailable = computed(() => selectedCategories.value.size || weInsideCatalog);
const loading = ref(false);

await Promise.all([
  loadCategoryTree(),
  loadAllBrands(),
  loadProductConditions(),
]);

function openSecondScreen(screenType: IScreenType) {
  secondScreenType.value = screenType;
  secondScreenIsShown.value = true;
}

function getCategoryIdForAttributes() {
  if (weInsideCatalog) return categoryId.value;

  const selectedCategory = getCategoryById(+[...selectedCategories.value][0]);

  return selectedCategory?.hasChildren ? selectedCategory!.id : selectedCategory!.parentId;
}

async function loadAvailableFilters() {
  let params;

  if (props.profileId) {
    params = { sellerId: props.profileId };
  } else {
    params = weInsideCatalog || weInsideBrand ? { ...props.pageInfo } : { sellerId: account.value.id };
  }

  await getAvailableFilters(params, toRaw(selectedFilters.value));

  availableCategories.value = availableFilters.value!.category!;
  availableSizes.value = availableFilters.value!.size!;
}

async function loadCategorySizes() {
  if (!categoryId.value) return;

  if (categoryIdIsAvailable.value) {
    allSizes.value = await fetchSizes(getCategoryIdForAttributes()!);
  } else {
    allSizes.value = [];
    availableSizes.value = [];
  }
}

async function loadAttributes() {
  if (!categoryId.value) return;

  if (categoryIdIsAvailable.value) {
    attributes.value = await fetchAttributes(getCategoryIdForAttributes()!);
  } else {
    attributes.value = [];
  }
}

const secondScreenTitles: Record<TCheckboxQueryParamName, string> = {
  categories: t('components.catalog.catalogFiltersMobile.catalogFiltersMobile.secondScreenTitle.categories'),
  brand: t('components.catalog.catalogFiltersMobile.catalogFiltersMobile.secondScreenTitle.brand'),
  size: t('components.catalog.catalogFiltersMobile.catalogFiltersMobile.secondScreenTitle.size'),
  productCondition: t('components.catalog.catalogFiltersMobile.catalogFiltersMobile.secondScreenTitle.productCondition'),
  isPro: t('components.catalog.catalogFiltersMobile.catalogFiltersMobile.secondScreenTitle.isPro'),
  price: t('common.price'),
  filter: t('components.catalog.catalogFiltersMobile.catalogFiltersMobile.secondScreenTitle.filter'),
};

const secondScreenTitle = computed(() => {
  if (secondScreenType.value?.type === 'filter') return attributes.value?.find((a) => a.id === secondScreenType.value?.id)?.name;

  return secondScreenType.value ? secondScreenTitles[secondScreenType.value.type] : '';
});

function showProducts() {
  const { category: categoriesIds, ...otherFilters } = toRaw(selectedFilters.value);
  let catalog:string[] = [];
  const query = Object.entries(otherFilters).reduce<Record<string, any>>((acc, [
    key,
    item,
  ]) => {
    if (isDefined(item) && !(Array.isArray(item) && item.length === 0)) {
      acc[key] = item;
    }

    return acc;
  }, {});
  const currentCategory = getCategoryById(categoriesIds[0]);

  if (currentCategory) {
    if (!props.showAllCategories && currentCategory.deep < 3) {
      catalog = currentCategory.url?.split('/').map((name) => name) || [];
      delete query.categoriesIds;
    } else {
      const parentCategory = getCategoryById(currentCategory.parentId!);

      catalog = parentCategory.url?.split('/').map((name) => name) || [];
      query.categoriesIds = categoriesIds.join(',');
    }
  }

  query.sort = currentSortingMethod.value;

  emit('close');
  window.scrollTo(0, 0);
  router.push({ query, params: { catalog } });
}

async function fetchData() {
  try {
    loading.value = true;

    await Promise.all([
      loadAvailableFilters(),
      loadCategorySizes(),
      loadAttributes(),
    ]).then(() => setFilters());
  } finally {
    loading.value = false;
  }
}

watch(() => selectedFilters.value, () => {
  fetchData();
}, { deep: true });

onMounted(() => {
  document.body.classList.add('overflow-hidden');
  window.usedeskMessenger?.toggle();

  const category = (route.query.categoriesIds as string)?.split(',').map((id) => parseInt(id, 10)) ?? (props.currentCategoryId ? [props.currentCategoryId] : []);
  const brand = props.pageInfo?.interestingBrands ? [parseInt(props.pageInfo.interestingBrands, 10)] : selectedFilters.value.brand;

  selectedFilters.value = {
    ...selectedFilters.value,
    category,
    brand,
  };
});
onUnmounted(() => {
  document.body.classList.remove('overflow-hidden');
  window.usedeskMessenger?.toggle(true);
});
</script>

<style scoped lang="scss">
.catalog-filters-mobile {
  &__buttons {
    width: 100%;
    height: 50px;
    display: flex;
    border-top: 1px solid #f0f0f0;
  }

  &__button {
    width: 100%;
    font-size: 12px;
  }
}
</style>
